import type { AccessLevel } from '@/interfaces/Access.interface';
import type { UnzonedDateTime } from '@/interfaces/Date.interface';
import type { ProfileId } from '@/interfaces/Profile.interface';
import type { UserId } from '@/interfaces/User.interface';
import type { APIError } from '@/providers/errors.constants';
import type { Branded } from '@/utils/typing/branded.type';

export type AlertScenarioId = Branded<number, 'AlertScenarioId'>;

export interface AlertScenario {
    scenarioId: AlertScenarioId;
    type: AlertScenarioType;
    status: AlertScenarioStatus;
    name: string;
    access: AccessLevel;
}

export enum AlertScenarioType {
    THRESHOLD = 'threshold',
    WATER_LEAKAGE = 'water_leakage',
    DRIFT = 'drift'
}

export interface AlertValidation {
    errors: APIError[];
    valid: boolean;
}

export enum AlertScenarioStatus {
    DRAFT = 'draft',
    PUBLISHED = 'published',
    DISABLED = 'disabled'
}

export enum AlertStatPeriodCode {
    THIS_WEEK = 'THIS_WEEK',
    LAST_WEEK = 'LAST_WEEK',
    THIS_MONTH = 'THIS_MONTH',
    LAST_MONTH = 'LAST_MONTH'
}

export enum ScenarioAccess {
    ADMIN = 'admin',
    READ = 'read',
    WRITE = 'write'
}

export enum ScenarioPriority {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH'
}

export interface ScenarioSharing {
    users: ScenarioSharingUser[];
    profiles: ScenarioSharingProfile[];
}

interface ScenarioResourceSharing {
    priority: ScenarioPriority;
    access: ScenarioAccess;
}
export interface ScenarioSharingUser extends ScenarioResourceSharing {
    userId: UserId;
}

export interface ScenarioSharingProfile extends ScenarioResourceSharing {
    profileId: ProfileId;
}

export interface ScenarioActivity {
    occupancy: AlertOccupancyMode;
    alertHolidays: boolean;
    alertPublicHolidays: boolean;
    periodStart: DayMonth;
    periodEnd: DayMonth;
    weeklyRanges: {
        isActivated: boolean;
        ranges: DayRange[];
    }[];
}

export interface DayMonth {
    day: number;
    month: number;
}

export interface DayRange {
    start: string;
    end: string;
}

export enum AlertOccupancyMode {
    OCCUPANCY = 'OCCUPANCY',
    INOCCUPANCY = 'INOCCUPANCY',
    ALL = 'ALL'
}

export enum AlertTypeCode {
    INFERIOR_THRESHOLD = 'inferior_threshold',
    SUPERIOR_THRESHOLD = 'superior_threshold',
    WATER_LEAKAGE = 'water_leakage',
    WATER_LEAKAGE_FORECASTING = 'water_leakage_forecasting',
    WATER_LEAKAGE_USER_DETECTED = 'water_leakage_user_detected'
}

export interface TimeseriesPoint {
    datetime: UnzonedDateTime;
    value: number;
}

export interface BoundsTimeseriesPoint {
    datetime: UnzonedDateTime;
    lowBound: number;
    highBound: number;
}

export interface NullableTimeseriesPoint {
    datetime: UnzonedDateTime;
    value: number | null;
}

export interface ScenarioBuildingOccupancyTimeseries {
    datetime: UnzonedDateTime;
    activeAfter: boolean;
    inactivityStatuses: string[];
}

export enum AlertSortableColumn {
    deviationVal = 'deviationVal',
    alertCount = 'alertCount',
    sensorCount = 'sensorCount',
    abruptAlertCount = 'abruptAlertCount',
    alertVal = 'alertVal'
}
